import { useEffect } from 'react';
import { Checkbox, FormControlLabel, Grid, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { skipToken } from '@reduxjs/toolkit/query';
import dayjs from 'dayjs';
import { commonInputStyle } from '../../../components/CustomFormsComponents/CustomField';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import useContracts from '../../../components/layout/ContractSelector/hook/useContracts';
import {
    useGetEmpresasQuery,
    useGetProvincesQuery,
    useLazyGetCitiesQuery
} from '../../../services/companyApi';
import { useGetOrganismsQuery } from '../../../services/organismsApi';
import { useGetTiposVisitasQuery } from '../../../services/visitasApi';
import { VisitaEstado, VisitStatus } from '../../Visitas/consts/visitasStatus';
import { VisitasRoamFiltros } from '../hooks/useVisitasRoam';

interface Props {
    filtros: VisitasRoamFiltros;
    onFiltroChange: (campo: keyof VisitasRoamFiltros, valor: string | number | boolean | undefined) => void;
    isShowMoreFiltersOpen: boolean;
}

function FiltrosVisitasRoam({ filtros, onFiltroChange, isShowMoreFiltersOpen }: Props) {
    const { contracts } = useContracts();

    // El filtro va por id de organismo central y no por nro de contrato: varios
    // organismos comparten el mismo número (p. ej. 10000) y el filtro mostraba y
    // traía otro contrato (SMP-482, mismo caso que SMP-451 en Visitas).
    const contractOptions = contracts.map((c: any) => ({
        id: c.id,
        nombre: c.nombre
    }));

    const idOrganismoCentralSeleccionado = filtros.idOrganismoCentral;

    const {
        data: { data: { data: organismsRaw } } = { data: { data: [] } },
        isFetching: isFetchingOrganisms
    } = useGetOrganismsQuery({
        idOrganizacion: idOrganismoCentralSeleccionado || undefined,
        skip: 0,
        take: 1000
    });

    const { data: tiposVisitasData, isFetching: isFetchingTipos } = useGetTiposVisitasQuery(null);

    const { data: provincesList } = useGetProvincesQuery(null);
    const [getCities, { data: citiesList }] = useLazyGetCitiesQuery();

    useEffect(() => {
        if (filtros.idProvinciaAccidente) {
            getCities({ provinceId: filtros.idProvinciaAccidente });
        }
    }, [filtros.idProvinciaAccidente, getCities]);

    const organismOptions = (organismsRaw as any[] || []).map((o: any) => ({
        id: o.id,
        nombre: o.razonSocial || o.nombre || String(o.id)
    }));

    const empresasQueryParams = idOrganismoCentralSeleccionado || filtros.idOrganismo
        ? {
              idOrganismoCentral: idOrganismoCentralSeleccionado || undefined,
              idOrganismo: filtros.idOrganismo || undefined,
              skip: 0,
              take: 1000
          }
        : skipToken;

    const { data: establishmentsData, isFetching: isFetchingEstablishments } =
        useGetEmpresasQuery(empresasQueryParams);

    const establishmentOptions = (establishmentsData?.data?.data || []).map((est: any) => ({
        id: est.id,
        nombre: est.nombre
    }));

    const tipoVisitaOptions = (tiposVisitasData?.data || []).map((t: any) => ({
        id: t.id,
        nombre: t.descripcion || String(t.id)
    }));

    const provinces =
        provincesList?.data?.provincias?.map((p: { id: number; nombre: string }) => ({
            nombre: p.nombre,
            id: p.id
        })) || [];

    const cities = Array.isArray(citiesList?.data?.localidades)
        ? citiesList.data.localidades.map(
              (c: { id: number; nombre: string; codigoPostal: number }) => ({
                  nombre: `${c.nombre} - ${c.codigoPostal}`,
                  id: c.id
              })
          )
        : [];

    const estadoOptions = Object.entries(VisitStatus)
        .filter(([key]) => Number(key) !== VisitaEstado.INFORMADO_SRT)
        .map(([key, value]) => ({
            id: Number(key),
            nombre: value as string
        }));

    return (
        <Grid container spacing={2} alignItems="flex-end">
            {/* Fila 1 - siempre visible */}
            <Grid item xs={12} sm={4}>
                <AutocompleteComponent
                    label="Contrato"
                    options={contractOptions}
                    value={
                        contractOptions.find((c) => c.id === filtros.idOrganismoCentral) || null
                    }
                    onChange={(option: any) =>
                        onFiltroChange('idOrganismoCentral', option?.id ?? undefined)
                    }
                    templateLabel={(o: any) => o.nombre}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <AutocompleteComponent
                    label="Organismo"
                    options={organismOptions}
                    value={organismOptions.find((o: any) => o.id === filtros.idOrganismo) || null}
                    loading={isFetchingOrganisms}
                    onChange={(option: any) =>
                        onFiltroChange('idOrganismo', option?.id ?? undefined)
                    }
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <AutocompleteComponent
                    label="Establecimiento"
                    options={establishmentOptions}
                    value={
                        establishmentOptions.find((e: any) => e.id === filtros.idEstablecimiento) ||
                        null
                    }
                    loading={isFetchingEstablishments}
                    onChange={(option: any) =>
                        onFiltroChange('idEstablecimiento', option?.id ?? undefined)
                    }
                />
            </Grid>

            {isShowMoreFiltersOpen && (
                <>
                    {/* Fila 2 */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Lugar de ocurrencia (calle)"
                            value={filtros.lugarOcurrencia ?? ''}
                            onChange={(e) => onFiltroChange('lugarOcurrencia', e.target.value)}
                            size="small"
                            sx={{ ...commonInputStyle }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <AutocompleteComponent
                            label="Provincia del accidente"
                            options={provinces}
                            value={provinces.find((p: any) => p.id === filtros.idProvinciaAccidente) || null}
                            onChange={(option: any) =>
                                onFiltroChange('idProvinciaAccidente', option?.id ?? undefined)
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <AutocompleteComponent
                            label="Localidad del accidente"
                            options={cities}
                            value={cities.find((c: any) => c.id === filtros.idLocalidadAccidente) || null}
                            onChange={(option: any) =>
                                onFiltroChange('idLocalidadAccidente', option?.id ?? undefined)
                            }
                            disabled={!filtros.idProvinciaAccidente}
                        />
                    </Grid>

                    {/* Fila 3 */}
                    <Grid item xs={12} sm={4}>
                        <AutocompleteComponent
                            label="Estado"
                            options={estadoOptions}
                            value={estadoOptions.find((o) => o.id === filtros.estado) || null}
                            onChange={(option: any) =>
                                onFiltroChange('estado', option?.id ?? undefined)
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Empleado (nombre o CUIL)"
                            value={filtros.empleado ?? ''}
                            onChange={(e) => onFiltroChange('empleado', e.target.value)}
                            size="small"
                            sx={{ ...commonInputStyle }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Nro ROAM"
                            value={filtros.nroRoam ?? ''}
                            onChange={(e) => onFiltroChange('nroRoam', e.target.value)}
                            size="small"
                            sx={{ ...commonInputStyle }}
                        />
                    </Grid>

                    {/* Fila 4 */}
                    <Grid item xs={12} sm={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Fecha de visita"
                                value={filtros.fechaVisita ? dayjs(filtros.fechaVisita) : null}
                                onChange={(val) =>
                                    onFiltroChange(
                                        'fechaVisita',
                                        val ? val.format('YYYY/MM/DD') : undefined
                                    )
                                }
                                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { ...commonInputStyle } } }}
                                format="DD/MM/YYYY"
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <AutocompleteComponent
                            label="Tipo de visita"
                            options={tipoVisitaOptions}
                            value={
                                tipoVisitaOptions.find((t: any) => t.id === filtros.idTipoVisita) ||
                                null
                            }
                            loading={isFetchingTipos}
                            onChange={(option: any) =>
                                onFiltroChange('idTipoVisita', option?.id ?? undefined)
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} display="flex" alignItems="center">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filtros.vencidas ?? false}
                                    onChange={(e) =>
                                        onFiltroChange('vencidas', e.target.checked || undefined)
                                    }
                                />
                            }
                            label="Vencida"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filtros.proximoVencer ?? false}
                                    onChange={(e) =>
                                        onFiltroChange(
                                            'proximoVencer',
                                            e.target.checked || undefined
                                        )
                                    }
                                />
                            }
                            label="Por vencer"
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
}

export default FiltrosVisitasRoam;
